import type { components } from "@/types/api.generated";
import apiClient from "./client";

/**
 * Eligibility axis (parallel to `verdict`): the tree's automatic **suggestion**
 * and the analyst's confirmed **verdict** share this 3-value enum. Tokens are EN
 * (label resolved in the UI, like `confidence`).
 */
export type EligibilityVerdict = "activate" | "decision" | "out_of_scope";

export interface ScopeActivationItem {
  scope_item_id: string;
  scope_item_name: string;
  lob: string;
  verdict: "activate" | "needs_decision" | "needs_review" | "skip";
  activated: boolean;
  confidence: string;
  clean_core: boolean;
  triggered_by: string[];
  s4_targets: string[];
  capabilities: string[];
  capability_details: ScopeCapabilityDetail[];
  // Eligibility tree suggestion (Fit-to-Standard). Absent on runs without a
  // country (the gate can't run). The analyst verdict is fetched separately.
  suggestion?: EligibilityVerdict | null;
  suggestion_reason?: string | null;
  // Tabelas que o SAP não liberou e que deixaram regra do item sem conclusão.
  // Ausente nas execuções anteriores à correção.
  refused_tables?: string[];
}

export interface ScopeCapabilityDetail {
  capability: string;
  triggered_by: string[];
  s4_target: string;
  decision_rule_text: string;
}

export interface ScopeActivationSummary {
  total: number;
  activate: number;
  needs_decision: number;
  needs_review: number;
  skip: number;
  clean_core: number;
  suggested_review: number;
}

export interface ScopeActivationResponse {
  version: string;
  summary: ScopeActivationSummary;
  items: ScopeActivationItem[];
  suggested_review: ScopeActivationItem[];
  fingerprint_tables: number;
  // Das sondadas, quantas o SAP não liberou. Ausente nas execuções anteriores.
  fingerprint_refused_tables?: number;
}

/** Avalia a aderência pela carga do ex-kit do projeto; sem conexão SAP desde a #1785. */
export async function assessScopeActivation(
  projectId: string,
  opts?: { country?: string; mandt?: string },
): Promise<ScopeActivationResponse> {
  const res = await apiClient.post<ScopeActivationResponse>(
    `/api/projects/${projectId}/scope-activation/assess`,
    { country: opts?.country, mandt: opts?.mandt },
  );
  return res.data;
}

// --- Analyst verdict (project_scope_decision) ------------------------------
// The analyst confirms/overrides the suggestion during the Fit-to-Standard
// review. Persisted per project × scope item so it survives agent re-runs.

/** De onde veio o veredito salvo: o analista na tela, o aceite em lote das
 * sugestões da árvore ou a planilha de revisão carregada do Excel (#1344). */
export type DecisionSource = "analyst" | "suggestion_accepted" | "excel_load";

export async function bulkScopeDecisions(
  projectId: string,
  decisions: Array<{
    scope_item_id: string;
    verdict: EligibilityVerdict;
    reason?: string | null;
    /** Vence o `source` do corpo quando vem; sem ele, o item é do analista. */
    source?: DecisionSource;
  }>,
): Promise<{ count: number }> {
  const res = await apiClient.post<{ count: number }>(
    `/api/projects/${projectId}/scope-decisions/bulk`,
    { decisions, source: "analyst" },
  );
  return res.data;
}

// --- Paginated scope-activation items (server-side, indexed) ---------------
// Cada linha traz o verdict ECC (evidência), a sugestão da árvore, o veredito
// do analista e o `effective_verdict` (analista → senão sugestão).

export interface ScopeItemRow {
  scope_item_id: string;
  scope_item_name: string | null;
  lob: string | null;
  verdict: string;
  confidence: string | null;
  clean_core: boolean;
  suggestion: EligibilityVerdict | null;
  suggestion_reason: string | null;
  analyst_verdict: EligibilityVerdict | null;
  analyst_reason: string | null;
  effective_verdict: EligibilityVerdict | null;
  /** `source` da decisão salva; `null` quando o item não tem decisão e o
   * efetivo é a sugestão (#1344). */
  decision_source?: DecisionSource | null;
  // Observação de curadoria do item (#1344). Todos `null` sem nota, e também
  // para quem está abaixo de `consultant` quando a nota é interna.
  curation_note?: string | null;
  show_in_report?: boolean | null;
  note_source?: NoteSource | null;
  note_updated_at?: string | null;
  note_updated_by_name?: string | null;
}

export interface ScopeItemsPage {
  items: ScopeItemRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ScopeItemsQuery {
  /** Omitida, o servidor resolve: a execução da aprovação ativa, senão a última
   * concluída (o mesmo critério de `/scope-activation/summary`). */
  executionId?: string;
  page?: number;
  limit?: number;
  verdict?: EligibilityVerdict;
  lob?: string;
  confidence?: string;
  search?: string;
  countryOnly?: boolean;
  hideActivated?: boolean;
  /** Só os itens com a marca Clean Core (drill-down do card do Fit-to-Standard). */
  cleanCore?: boolean;
}

function scopeItemsParams(q: ScopeItemsQuery): Record<string, string> {
  const p: Record<string, string> = {};
  if (q.executionId) p.execution_id = q.executionId;
  if (q.page != null) p.page = String(q.page);
  if (q.limit != null) p.limit = String(q.limit);
  if (q.verdict) p.verdict = q.verdict;
  if (q.lob) p.lob = q.lob;
  if (q.confidence) p.confidence = q.confidence;
  if (q.search) p.search = q.search;
  if (q.countryOnly != null) p.country_only = String(q.countryOnly);
  if (q.hideActivated) p.hide_activated = "true";
  if (q.cleanCore) p.clean_core = "true";
  return p;
}

export async function listScopeItems(
  projectId: string,
  q: ScopeItemsQuery,
): Promise<ScopeItemsPage> {
  const res = await apiClient.get<ScopeItemsPage>(
    `/api/projects/${projectId}/scope-activation/items`,
    { params: scopeItemsParams(q) },
  );
  return res.data;
}

/** O maior `limit` que `/scope-activation/items` aceita. */
const MAX_PAGE_SIZE = 200;

/**
 * Todas as linhas do filtro, página por página (#1344).
 *
 * O detalhe do Diagnóstico lista os ativos inteiros, e não uma página: ele tem
 * de fechar com o número do tile. Para quando junta o total ou quando uma
 * página volta vazia, porque uma gravação entre duas páginas pode encolher o
 * conjunto e o total da primeira deixaria de valer.
 */
export async function listAllScopeItems(
  projectId: string,
  q: Omit<ScopeItemsQuery, "page" | "limit">,
): Promise<ScopeItemRow[]> {
  const all: ScopeItemRow[] = [];
  for (let page = 1; ; page++) {
    const res = await listScopeItems(projectId, { ...q, page, limit: MAX_PAGE_SIZE });
    all.push(...res.items);
    if (res.items.length === 0 || all.length >= res.total) return all;
  }
}

// --- Curation note per scope item (project_scope_item_note) ----------------
// O consultor anota o item durante a curadoria. A nota vive por projeto × item,
// sobrevive a re-execuções, e `show_in_report = false` a deixa fora do relatório.

/** De onde veio a nota: a tela ou a planilha de revisão carregada do Excel. */
export type NoteSource = "ui" | "excel_load";

export interface ScopeItemNote {
  scope_item_id: string;
  curation_note: string;
  show_in_report: boolean;
  source: NoteSource;
  note_updated_at: string;
  note_updated_by_name?: string | null;
}

export interface ScopeItemNoteIn {
  scope_item_id: string;
  /** `null` apaga a nota. Texto em branco o servidor recusa. */
  curation_note: string | null;
  /** Omitido, mantém o valor salvo; nota nova nasce exibida no relatório. */
  show_in_report?: boolean;
}

/** Todas as notas do projeto, internas incluídas (rota de `consultant`). */
export async function listScopeItemNotes(projectId: string): Promise<ScopeItemNote[]> {
  const res = await apiClient.get<{ notes: ScopeItemNote[] }>(
    `/api/projects/${projectId}/scope-item-notes`,
  );
  return res.data.notes;
}

/** Grava as notas com texto e apaga as `null`, numa transação só. */
export async function bulkScopeItemNotes(
  projectId: string,
  notes: ScopeItemNoteIn[],
  opts?: { source?: NoteSource },
): Promise<{ upserted: number; deleted: number }> {
  const res = await apiClient.post<{ upserted: number; deleted: number }>(
    `/api/projects/${projectId}/scope-item-notes/bulk`,
    { notes, source: opts?.source ?? "ui" },
  );
  return res.data;
}

// --- Carga do CSV do Catálogo (sheet-load) ---------------------------------
// O navegador lê o arquivo e manda as linhas; o servidor grava tudo numa
// transação só, e só o que mudou. O conteúdo do arquivo não é gravado; fica o
// registro da carga (nome e contagens).

/** Máximo de linhas por carga; espelha `MAX_ROWS` em
 * `services/api/app/routers/scope_review_sheet.py`. */
export const SHEET_MAX_ROWS = 5000;

/** Tamanho máximo do `scope_item_id`; espelha o `max_length` do campo em
 * `SheetLoadRowIn`, no mesmo arquivo do backend. */
export const SCOPE_ITEM_ID_MAX_LENGTH = 10;

export type ScopeSheetLoadErrorCode =
  | "too_large"
  | "empty_file"
  | "missing_id_column"
  | "no_editable_column"
  | "missing_id"
  | "unknown_verdict"
  | "duplicate_scope_item"
  | "note_too_long"
  | "unknown_scope_item"
  | "unclosed_quote"
  | "duplicate_header"
  | "missing_editable_cell"
  | "too_many_rows"
  | "id_too_long"
  | "invalid_payload"
  | "invalid_encoding"
  | "foreign_export";

/** Um problema do arquivo. `line` é a linha da planilha (cabeçalho = 1); `null` é o arquivo todo. */
export interface ScopeSheetLoadError {
  line: number | null;
  scope_item_id?: string | null;
  code: ScopeSheetLoadErrorCode;
  value?: string | null;
}

/** `verdict` `null` não mexe; `note` vazia remove a nota; campo ausente é coluna ausente. */
export type ScopeSheetLoadRow = components["schemas"]["SheetLoadRowIn"];
export type ScopeSheetLoadIn = components["schemas"]["SheetLoadIn"];
export type ScopeSheetLoadOut = components["schemas"]["SheetLoadOut"];

export async function loadScopeSheet(
  projectId: string,
  body: ScopeSheetLoadIn,
): Promise<ScopeSheetLoadOut> {
  // Com teto: sem ele, uma carga travada deixaria a tela bloqueada sem aviso.
  const res = await apiClient.post<ScopeSheetLoadOut>(
    `/api/projects/${projectId}/scope-activation/sheet-load`,
    body,
    { timeout: 120_000 },
  );
  return res.data;
}

/**
 * Erros de um 422 da carga, já na linha da planilha; `null` para qualquer outra falha.
 *
 * Lê os dois formatos de 422 da rota: `detail.errors`, da regra da carga (id
 * desconhecido ou repetido), e `detail` em lista, da validação do FastAPI. No
 * segundo, `loc` aponta o índice em `rows`, e `rows` (as linhas enviadas)
 * traduz esse índice na linha da planilha; o tipo do erro vira o código da
 * tela (`id_too_long`, `missing_id`, `note_too_long`, `unknown_verdict`,
 * `too_many_rows`), e o resto, `invalid_payload`.
 */
export function sheetLoadErrors(
  error: unknown,
  rows: ScopeSheetLoadRow[] = [],
): ScopeSheetLoadError[] | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status !== 422) return null;
  const detail = response.data?.detail;
  const errors = (detail as { errors?: unknown } | undefined)?.errors;
  if (Array.isArray(errors)) return errors as ScopeSheetLoadError[];
  if (!Array.isArray(detail)) return null;
  return detail.map((issue: { loc?: (string | number)[]; type?: string; input?: unknown }) => {
    const location = issue.loc ?? [];
    const index = location[1] === "rows" && typeof location[2] === "number" ? location[2] : null;
    const field = location.at(-1);
    if (index == null && field === "rows" && issue.type === "too_long") {
      return { line: null, code: "too_many_rows" };
    }
    const row = index == null ? undefined : rows[index];
    let code: ScopeSheetLoadErrorCode = "invalid_payload";
    if (index != null) {
      if (field === "verdict") code = "unknown_verdict";
      if (field === "note" && issue.type === "string_too_long") code = "note_too_long";
      if (field === "scope_item_id") {
        code = issue.type === "string_too_long" ? "id_too_long" : "missing_id";
      }
    }
    return {
      line: row?.line ?? null,
      ...(row ? { scope_item_id: row.scope_item_id } : {}),
      code,
      ...(code === "unknown_verdict" ? { value: String(issue.input ?? "") } : {}),
    };
  });
}

/** Aceita a sugestão como veredito para TODO o conjunto filtrado (server-side). */
export async function acceptSuggestions(
  projectId: string,
  q: ScopeItemsQuery & { executionId: string },
): Promise<{ count: number }> {
  const res = await apiClient.post<{ count: number }>(
    `/api/projects/${projectId}/scope-activation/accept-suggestions`,
    null,
    { params: scopeItemsParams(q) },
  );
  return res.data;
}

// --- Agregado server-side (veredito efetivo) ------------------------------
// Contagens/motivos/LoB pelo veredito EFETIVO (analista → senão sugestão),
// já com o filtro de país. É o que o Overview e os contadores das abas leem —
// reflete as edições do analista + aprovação sem trazer o payload do run.

export interface ScopeSummaryReason {
  verdict: EligibilityVerdict;
  reason: string;
  count: number;
}

export interface ScopeSummaryLob {
  lob: string;
  activate: number;
  decision: number;
  out_of_scope: number;
  total: number;
}

export interface ScopeSummary {
  total: number;
  excluded_by_country: number;
  counts: Record<EligibilityVerdict, number>;
  reasons: ScopeSummaryReason[];
  lobs: ScopeSummaryLob[];
  ecc_activate: number;
  // Progresso da revisão: pontos de decisão (suggestion=decision) x resolvidos.
  decisions_total: number;
  decisions_resolved: number;
}

export async function getScopeSummary(
  projectId: string,
  opts: { executionId: string; countryOnly?: boolean },
): Promise<ScopeSummary> {
  const res = await apiClient.get<ScopeSummary>(
    `/api/projects/${projectId}/scope-activation/summary`,
    {
      params: {
        execution_id: opts.executionId,
        ...(opts.countryOnly != null
          ? { country_only: String(opts.countryOnly) }
          : {}),
      },
    },
  );
  return res.data;
}

// --- Aprovação formal do escopo (congela o projeto) -----------------------

export interface ScopeApproval {
  approved: boolean;
  version: number | null;
  approved_by: string | null;
  approved_at: string | null;
}

export async function getScopeApproval(
  projectId: string,
): Promise<ScopeApproval> {
  const res = await apiClient.get<ScopeApproval>(
    `/api/projects/${projectId}/scope-activation/approval`,
  );
  return res.data;
}

/** Aprova (congela) o escopo: snapshot versionado do veredito efetivo. */
export async function approveScope(
  projectId: string,
  executionId: string,
): Promise<ScopeApproval> {
  const res = await apiClient.post<ScopeApproval>(
    `/api/projects/${projectId}/scope-activation/approve`,
    null,
    { params: { execution_id: executionId } },
  );
  return res.data;
}

/** Reabre o escopo aprovado (volta a Draft). Dev: qualquer um; futuro: admin. */
export async function reopenScope(projectId: string): Promise<ScopeApproval> {
  const res = await apiClient.post<ScopeApproval>(
    `/api/projects/${projectId}/scope-activation/reopen`,
  );
  return res.data;
}

// --- Trava de reabertura (catraca) -----------------------------------------
// Gate 3: uma vez que o Configuration Server publicou (`ready`) uma extração
// em cima deste escopo, reabrir o Functional pra editar o veredito destrava
// um scope que já virou config — a UI trava o botão em vez de deixar o
// analista descobrir isso só no 409 do backend.

export interface ConfigExtractionStatus {
  has_ready_extraction: boolean;
}

/**
 * Existe extração de configuração publicada neste projeto?
 *
 * Pergunta ao backend com `status=ready&limit=1` em vez de varrer a lista
 * paginada: com mais de uma página de extrações, uma `ready` antiga cairia
 * fora da página e a trava sumiria da tela sem nenhum sinal.
 */
export async function getConfigExtractionStatus(
  projectId: string,
): Promise<ConfigExtractionStatus> {
  const res = await apiClient.get<{ status: string }[]>(
    `/api/projects/${projectId}/configuration-server/extractions`,
    { params: { status: "ready", limit: 1 } },
  );
  return { has_ready_extraction: res.data.length > 0 };
}

/**
 * Reopen deve ficar travado até a resposta confirmar o contrário.
 *
 * `status` chega `undefined` enquanto a query ainda está em voo — isso não
 * é "sem extração ready", é "ainda não sei". Tratar como liberado nessa
 * janela deixaria o botão habilitado bem no intervalo que o gate 3 existe
 * para fechar (o 409 do backend cobre a falha de verdade, mas o gate visual
 * falharia no caso exato para o qual foi construído).
 */
export function isReopenBlocked(status: ConfigExtractionStatus | undefined): boolean {
  return status?.has_ready_extraction ?? true;
}
